import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCurrentUser } from "../services/authApi";
import type { LoadedConnectionId } from "../services/loadedConnection";
import { viewerKindOf } from "../services/viewerKind";
import type { PropertyAccessRole, ViewerKind } from "../types/config";

/** How the caller reaches the property open: their role on it, and a viewer's kind of grant. */
export interface OpenPropertyAccess {
    role: PropertyAccessRole | null;
    viewerKind: ViewerKind | null;
}

export interface OpenPropertyCheck {
    /**
     * Another property is open now than the one the tab was loaded for. The
     * shell shows `PropertyOpenedElsewhereNotice` in place of the page.
     */
    changed: boolean;
    /**
     * The property the service named as open when it said so — its id, or
     * null for none — so the notice can name it. Undefined while nothing
     * changed.
     */
    openConnectionId: number | null | undefined;
    /**
     * Ask the service which property is open, and compare it with the tab's.
     * A question already out is superseded: whatever prompted this one — a
     * write refused, the editor opening, the tab's own property opened again —
     * happened after it was asked, so its answer no longer counts.
     */
    check: () => void;
    /**
     * The access the latest answer named for the property the tab was loaded
     * for, while that property is still the one open — a new object with
     * every answer, so a reader can compare it with the access the pages were
     * loaded with. Undefined before an answer, and while another is open.
     */
    openAccess: OpenPropertyAccess | undefined;
}

/**
 * Whether the property the tab was loaded for is still the one open.
 *
 * The open property is server-side state every tab of a user shares — every
 * window and device too — and a page reads it once, when it loads. A tab
 * looked at again after another tab opened a second property holds the
 * first one's data while every read it makes from then on answers for the
 * second: the strategy editor showed BH Test Eda's strategy over Art Hotel's
 * room types, which filtered out every room type it had (30 Sep 2026). So the
 * shell asks again whenever the tab is looked at — shown, or its window
 * focused — and whenever something asks it to (`openPropertyEvents.ts`), and
 * nothing while it does not know which property it was loaded for.
 *
 * A mismatch is recorded against the property the tab was loaded for, so a
 * tab loaded again for the property now open is that property's tab again. A
 * question that fails keeps what was known; showing and focusing the tab
 * together ask once.
 */
export function useOpenPropertyCheck(loaded: LoadedConnectionId): OpenPropertyCheck {
    // Recorded with the property it was measured against, which can itself be
    // null (no property open) or undefined (not known).
    const [staleFor, setStaleFor] = useState<{ loaded: LoadedConnectionId; open: number | null } | null>(null);
    const [access, setAccess] = useState<{ loaded: LoadedConnectionId; access: OpenPropertyAccess } | null>(null);
    const loadedRef = useRef(loaded);
    loadedRef.current = loaded;
    // The question whose answer counts, and whether it is still out.
    const latestQuestionRef = useRef(0);
    const askingRef = useRef(false);
    const mountedRef = useRef(true);

    const ask = useCallback((supersede: boolean) => {
        const expected = loadedRef.current;
        if (expected === undefined) return;
        if (askingRef.current && !supersede) return;
        const question = ++latestQuestionRef.current;
        askingRef.current = true;
        fetchCurrentUser()
            .then((user) => {
                if (!mountedRef.current || question !== latestQuestionRef.current) return;
                const same = user.activeConnectionId === expected;
                setStaleFor(same ? null : { loaded: expected, open: user.activeConnectionId });
                // The access is said only of the property still open: another
                // one open is the notice's to say, whatever access it gives.
                setAccess(same
                    ? {
                        loaded: expected,
                        access: {
                            role: user.activeConnectionRole ?? null,
                            // Read as the shell reads it (`useDashboardInit`):
                            // a server that names no kind is `management`, and
                            // no role but a viewer's has one. Compared raw, an
                            // unchanged access would read as a changed one.
                            viewerKind: viewerKindOf(user.activeConnectionRole, user.activeConnectionViewerKind),
                        },
                    }
                    : null);
            })
            .catch(() => {
                // Not knowing is no answer: keep what was known.
            })
            .finally(() => {
                if (question === latestQuestionRef.current) askingRef.current = false;
            });
    }, []);

    const check = useCallback(() => ask(true), [ask]);

    useEffect(() => {
        mountedRef.current = true;
        // Looking at the tab asks nothing new while a question is out: being
        // shown and being focused arrive together.
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") ask(false);
        };
        const onFocus = () => ask(false);
        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("focus", onFocus);
        return () => {
            mountedRef.current = false;
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("focus", onFocus);
        };
    }, [ask]);

    const changed = staleFor !== null && staleFor.loaded === loaded;
    return {
        changed,
        openConnectionId: changed ? staleFor.open : undefined,
        check,
        openAccess: !changed && access !== null && access.loaded === loaded ? access.access : undefined,
    };
}
